// frontend/src/pages/daily-tug-report/index.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api.js';
import useOfflineQueue from '../../hooks/useOfflineQueue.js';
import SafetyIncidents from '../../components/SafetyIncidents.jsx';
import TugInventory from '../../components/TugInventory.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { deckProblems, engineProblems } from './required.js';

const QUEUE_KEY = 'tugos.dtr.queue';
const TUG_KEY = 'tugos.dtr.tugId';
const ME = [['Assist', 'Assist'], ['FreeRun', 'Free run'], ['Shifting', 'Shifting'], ['Towing', 'Towing'], ['SeaTrial', 'Sea trial']];
const AE = [['1', 'A/E No.1'], ['2', 'A/E No.2'], ['3', 'A/E No.3'], ['FireMonitor', 'Fire monitor']];
const num = (v) => Number(v) || 0;
const round2 = (v) => Math.round(v * 100) / 100;
const manilaToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Manila' }).format(new Date());

/** Same arithmetic as backend dtr.calc.js, for live totals while typing. */
function liveTotals(e) {
  const meFuel = ME.reduce((a, [k]) => a + num(e[`meFuel${k}`]), 0);
  const aeFuel = AE.reduce((a, [k]) => a + num(e[`aeFuel${k}`]), 0);
  const meHours = ME.reduce((a, [k]) => a + num(e[`meHours${k}`]), 0);
  const aeHours = AE.reduce((a, [k]) => a + num(e[`aeHours${k}`]), 0);
  // Only tank lines with a figure count (a remembered tank name is not 0 litres).
  const tanks = (e.tankSoundings || []).filter((t) => t.litres !== '' && t.litres != null);
  const sounded = tanks.length ? tanks.reduce((a, t) => a + num(t.litres), 0) : e.fuelSoundedL === '' || e.fuelSoundedL == null ? null : num(e.fuelSoundedL);
  const opening = e.fuelOpeningL === '' || e.fuelOpeningL == null ? null : num(e.fuelOpeningL);
  const total = meFuel + aeFuel;
  const expected = opening == null ? null : opening - total + num(e.fuelSuppliedL) + num(e.fuelFromTugsL) - num(e.fuelTransferredL);
  const variance = sounded == null || expected == null ? null : round2(sounded - expected);
  return { meFuel: round2(meFuel), aeFuel: round2(aeFuel), meHours: round2(meHours), aeHours: round2(aeHours), total: round2(total), sounded, variance };
}

function Field({ label, id, value, onChange, disabled, type = 'number', step = 'any', required = false }) {
  return (
    <div className="col-6 col-md-3 mb-2">
      <label htmlFor={`dtr-${id}`} className="form-label small fw-bold text-muted mb-1">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      <input
        id={`dtr-${id}`}
        type={type}
        step={type === 'number' ? step : undefined}
        min={type === 'number' ? 0 : undefined}
        inputMode={type === 'number' ? 'decimal' : undefined}
        className="form-control form-control-sm"
        value={value ?? ''}
        onChange={(ev) => onChange(ev.target.value)}
        disabled={disabled}
        data-testid={`dtr-${id}`}
      />
    </div>
  );
}

const FIELD_LABEL = (f) => f.replace(/_/g, ' ').replace(/\bl$/, '(L)').replace(/^me /, 'M/E ').replace(/^ae /, 'A/E ');
const fmtWhen = (ts) => (ts ? new Date(ts).toLocaleString() : '—');

/**
 * The crew's own page, and — given `office` ({ tugId, date, onClose, onSaved }) —
 * the office's correction view of any tug's submitted report: same form, a
 * reason for every save, and the history of what was changed.
 */
export default function DailyTugReportPage({ office = null }) {
  const [report, setReport] = useState(null);
  const [engine, setEngine] = useState({});
  const [deck, setDeck] = useState({});
  const [tugs, setTugs] = useState([]);
  const [ports, setPorts] = useState([]);
  const { user } = useAuth();
  const [tugId, setTugId] = useState(() => localStorage.getItem(TUG_KEY) || '');
  const [date, setDate] = useState(''); // '' = today (Manila)
  const [engineUnlocked, setEngineUnlocked] = useState(false);
  const [needsTug, setNeedsTug] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState({ engine: false, deck: false });
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [missing, setMissing] = useState({ engine: [], deck: [] });

  // A queued save the server refuses (changed elsewhere, locked, wrong tug) is
  // reported and dropped — retrying it forever would never succeed.
  const send = useCallback(async (item) => {
    try {
      return await api.saveDtrSection(item.section, item.body, item.date, item.tugId, item.ifSavedAt);
    } catch (err) {
      if (err.status >= 400 && err.status < 500) {
        setError(`A ${item.section} section saved offline for ${item.date} was not accepted: ${err.details?.[0]?.message || err.message}`);
        return null;
      }
      throw err;
    }
  }, []);
  // Per user, so a shared tablet never replays one person's save as another's.
  const { syncStatus, enqueueOrSend } = useOfflineQueue(`${QUEUE_KEY}.${user?.id || user?.email || 'anon'}`, send);

  // Only the most recent load may touch the form. The page can start more than
  // one (a re-mount, a quick change of date): an older response that arrives
  // late must never overwrite figures someone has begun typing.
  const loadSeq = useRef(0);
  const officeTugId = office?.tugId;
  const officeDate = office?.date;
  const load = useCallback(async (pickedTug, pickedDate) => {
    const seq = ++loadSeq.current;
    const latest = () => seq === loadSeq.current;
    setLoading(true);
    setError('');
    try {
      const r = officeTugId
        ? await api.getOfficeDtr(officeTugId, officeDate)
        : await api.getMyDtr(pickedDate || undefined, pickedTug || undefined);
      if (!latest()) return;
      setReport(r);
      setEngine(r.engine);
      setDeck(r.deck);
      setNeedsTug(false);
    } catch (err) {
      if (!latest()) return;
      if (/pick a tug/i.test(err.message || '')) {
        setNeedsTug(true);
        setTugs(await api.getDtrTugs());
      } else setError(err.message || 'Failed to load the report.');
    } finally {
      // The form stays hidden until the newest load has answered.
      if (latest()) setLoading(false);
    }
  }, [officeTugId, officeDate]);

  useEffect(() => {
    load(tugId, date);
    api.getPortConditions().then((c) => setPorts(c.ports)).catch(() => setPorts([]));
  }, [load, tugId, date]);

  const totals = useMemo(() => liveTotals(engine), [engine]);
  // A captain owns the deck section; the engine section stays read-only for them
  // until they say there is no engineer to fill it in.
  const captainCover = !office && Boolean(report?.canEdit.engine && report?.canEdit.deck);
  // The daily email locks a report for the crew; the office can still correct it.
  const open = office || !report?.locked;
  const canEngine = Boolean(report?.canEdit.engine) && open && (!captainCover || engineUnlocked);
  const canDeck = Boolean(report?.canEdit.deck) && open;
  const setE = (k) => (v) => { setEngine((p) => ({ ...p, [k]: v })); setSaved((s) => ({ ...s, engine: false })); };
  const setD = (k) => (v) => { setDeck((p) => ({ ...p, [k]: v })); setSaved((s) => ({ ...s, deck: false })); };

  const correct = async (section) => {
    setError('');
    if (reason.trim().length < 3) {
      setError('Say why the report is being corrected before saving.');
      return;
    }
    try {
      const next = await api.correctDtrSection(section, section === 'engine' ? engine : deck, report.reportDate, report.tug.id, reason.trim());
      setReport(next);
      setEngine(next.engine);
      setDeck(next.deck);
      setSaved((s) => ({ ...s, [section]: true }));
      office.onSaved?.();
    } catch (err) {
      setError(err.details?.[0]?.message || err.message || 'Could not save the correction.');
    }
  };

  const save = async (section) => {
    if (office) return correct(section);
    setError('');
    // Checked on the device first, so a report filled in offline is not queued
    // only to be refused when the signal returns.
    const problems = section === 'engine' ? engineProblems(engine, totals) : deckProblems(deck);
    setMissing((m) => ({ ...m, [section]: problems }));
    if (problems.length) return;
    const item = {
      section,
      body: section === 'engine' ? engine : deck,
      date: report.reportDate,
      tugId: report.tug.id,
      ifSavedAt: report.sections[section].savedAt || '',
    };
    if (!navigator.onLine) {
      await enqueueOrSend(item);
    } else {
      try {
        const next = await api.saveDtrSection(item.section, item.body, item.date, item.tugId, item.ifSavedAt);
        setReport(next);
        if (section === 'engine') setEngine(next.engine);
        else setDeck(next.deck);
      } catch (err) {
        // The server answered and said no (bad figure, locked, not allowed): show
        // it. Only a connection failure goes to the offline queue.
        if (err.status) {
          setError(err.details?.[0]?.message || err.message || 'Could not save the report.');
          return;
        }
        await enqueueOrSend(item);
      }
    }
    setSaved((s) => ({ ...s, [section]: true }));
  };

  const setList = (key, i, patch) => setE(key)((engine[key] || []).map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addRow = (key, blank) => setE(key)([...(engine[key] || []), blank]);
  const removeRow = (key, i) => setE(key)((engine[key] || []).filter((_r, idx) => idx !== i));

  if (loading) return <div className="container-fluid py-4" data-testid="dtr-page">Loading…</div>;

  if (needsTug) {
    return (
      <div className="container-fluid py-4" data-testid="dtr-page" style={{ maxWidth: 520 }}>
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <label htmlFor="dtr-tug-select" className="form-label fw-bold">Which tug are you reporting for?</label>
            <select
              id="dtr-tug-select"
              className="form-select"
              value=""
              onChange={(ev) => { localStorage.setItem(TUG_KEY, ev.target.value); setTugId(ev.target.value); }}
              data-testid="dtr-tug-select"
            >
              <option value="">Select a tug…</option>
              {tugs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>
    );
  }

  if (!report) return <div className="container-fluid py-4" data-testid="dtr-page"><div className="alert alert-danger">{error}</div></div>;

  return (
    <div className="container-fluid py-4" data-testid="dtr-page" style={{ maxWidth: 1100 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <h4 className="mb-0">{office ? 'Correct ' : ''}Daily Tug Report — {report.tug.name}</h4>
        <span className="text-muted small">
          {office && (
            <button type="button" className="btn btn-outline-secondary btn-sm me-2" onClick={office.onClose} data-testid="dtr-office-close">
              Close
            </button>
          )}
          <input
            type="date"
            className="form-control form-control-sm d-inline-block w-auto me-2"
            value={report.reportDate}
            max={manilaToday()}
            onChange={(ev) => setDate(ev.target.value)}
            disabled={Boolean(office)}
            aria-label="Report date"
            data-testid="dtr-date"
          />
          MSG #{report.msgNo ?? '—'}
          {!office && <> · <span data-testid="dtr-sync">{syncStatus}</span></>}
          {!office && !report.tug.assigned && (
            <button type="button" className="btn btn-link btn-sm" onClick={() => { localStorage.removeItem(TUG_KEY); setTugId(''); }} data-testid="dtr-change-tug">
              Change tug
            </button>
          )}
        </span>
      </div>
      {error && <div className="alert alert-danger" data-testid="dtr-error">{error}</div>}
      {report.locked && !office && <div className="alert alert-secondary" data-testid="dtr-locked">Locked — the daily email has been sent.</div>}
      {office && (
        <div className="card border-0 shadow-sm mb-3 border-start border-warning" data-testid="dtr-correction-panel">
          <div className="card-body">
            {report.locked && (
              <p className="small text-muted mb-2">
                This report has already gone out in the daily email. A correction updates the record and the live
                figures; the email already sent is not changed.
              </p>
            )}
            {!report.canEdit.deck && !report.canEdit.engine ? (
              <p className="small mb-0">The tug has not submitted this report yet, so there is nothing to correct.</p>
            ) : (
              <>
                <label htmlFor="dtr-correction-reason" className="form-label small fw-bold text-muted mb-1">
                  Reason for the correction <span className="text-danger">*</span>
                </label>
                <input
                  id="dtr-correction-reason"
                  className="form-control form-control-sm"
                  value={reason}
                  maxLength={1000}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="e.g. Chief engineer reported a typing error in M/E assist fuel"
                  data-testid="dtr-correction-reason"
                />
                <span className="small text-muted">
                  Change the figures below, then save the section. Each changed field is recorded with this reason.
                  {(!report.canEdit.deck || !report.canEdit.engine) &&
                    ` The ${report.canEdit.deck ? 'engine' : 'deck'} section has not been submitted yet and cannot be corrected.`}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body small row">
          <div className="col-6 col-md-3"><span className="text-muted d-block">Master</span>{report.shore.masterName || '—'}</div>
          <div className="col-6 col-md-3"><span className="text-muted d-block">Chief Engineer</span>{report.shore.chiefEngineerName || '—'}</div>
          <div className="col-6 col-md-3"><span className="text-muted d-block">Crew on board</span>{report.shore.crewCount}</div>
          <div className="col-6 col-md-3"><span className="text-muted d-block">Jobs performed</span>{report.shore.jobsPerformed}</div>
          {report.shore.shoreRemarks && (
            <div className="col-12 mt-2" data-testid="dtr-shore-remarks"><span className="text-muted d-block">Shore remarks</span>{report.shore.shoreRemarks}</div>
          )}
        </div>
      </div>

      {/* ---------------- Engine section ---------------- */}
      <div className="card border-0 shadow-sm mb-3" data-testid="dtr-engine">
        <div className="card-header bg-white pt-3 border-bottom-0 d-flex justify-content-between">
          <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Engine section</h6>
          <span className="small text-muted">{report.sections.engine.savedBy ? `Saved by ${report.sections.engine.savedBy}` : 'Not saved yet'}</span>
        </div>
        <div className="card-body">
          {captainCover && !engineUnlocked && !report.locked && (
            <div className="alert alert-light border d-flex justify-content-between align-items-center small" data-testid="dtr-engine-readonly">
              <span>The chief engineer fills in this section.</span>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEngineUnlocked(true)} data-testid="dtr-unlock-engine">
                No engineer on board — fill it in myself
              </button>
            </div>
          )}
          <h6 className="small fw-bold">Fuel on board</h6>
          <div className="row">
            <Field label="Yesterday (L)" id="fuelOpeningL" required value={engine.fuelOpeningL} onChange={setE('fuelOpeningL')} disabled={!canEngine || report.openingDerived} />
            {(engine.tankSoundings || []).length === 0 && (
              <Field label="Sounded today (L)" id="fuelSoundedL" required value={engine.fuelSoundedL} onChange={setE('fuelSoundedL')} disabled={!canEngine} />
            )}
            <Field label="Fresh water (tons)" id="freshWaterT" required value={engine.freshWaterT} onChange={setE('freshWaterT')} disabled={!canEngine} />
          </div>
          {(engine.tankSoundings || []).map((t, i) => (
            <div className="row g-2 mb-1" key={i} data-testid="dtr-tank-row">
              <div className="col-4"><input className="form-control form-control-sm" placeholder="Tank" value={t.tank} disabled={!canEngine} onChange={(ev) => setList('tankSoundings', i, { tank: ev.target.value })} data-testid={`dtr-tank-name-${i}`} /></div>
              <div className="col-3"><input className="form-control form-control-sm" placeholder="Reading" value={t.reading ?? ''} disabled={!canEngine} onChange={(ev) => setList('tankSoundings', i, { reading: ev.target.value })} /></div>
              <div className="col-3"><input type="number" min="0" className="form-control form-control-sm" placeholder="Litres" value={t.litres ?? ''} disabled={!canEngine} onChange={(ev) => setList('tankSoundings', i, { litres: ev.target.value })} data-testid={`dtr-tank-litres-${i}`} /></div>
              <div className="col-2"><button type="button" className="btn btn-sm btn-outline-secondary w-100" disabled={!canEngine} onClick={() => removeRow('tankSoundings', i)} aria-label="Remove tank">×</button></div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-primary mb-3" disabled={!canEngine} onClick={() => addRow('tankSoundings', { tank: '', reading: '', litres: '' })} data-testid="dtr-add-tank">Add tank</button>

          <h6 className="small fw-bold">Oils</h6>
          {(engine.consumables || []).map((c, i) => (
            <div className="row g-2 mb-1" key={i}>
              <div className="col-4"><input className="form-control form-control-sm" placeholder="Oil (e.g. LO40)" value={c.name} disabled={!canEngine} onChange={(ev) => setList('consumables', i, { name: ev.target.value })} /></div>
              <div className="col-3"><input type="number" min="0" className="form-control form-control-sm" placeholder="On board (L)" value={c.onBoardL ?? ''} disabled={!canEngine} onChange={(ev) => setList('consumables', i, { onBoardL: ev.target.value })} /></div>
              <div className="col-3"><input type="number" min="0" className="form-control form-control-sm" placeholder="Used (L)" value={c.usedL ?? ''} disabled={!canEngine} onChange={(ev) => setList('consumables', i, { usedL: ev.target.value })} /></div>
              <div className="col-2"><button type="button" className="btn btn-sm btn-outline-secondary w-100" disabled={!canEngine} onClick={() => removeRow('consumables', i)} aria-label="Remove oil">×</button></div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-outline-primary mb-3" disabled={!canEngine} onClick={() => addRow('consumables', { name: '', onBoardL: '', usedL: '' })}>Add oil</button>

          <h6 className="small fw-bold">Main engine — hours</h6>
          <div className="row">{ME.map(([k, l]) => <Field key={k} label={l} id={`meHours${k}`} value={engine[`meHours${k}`]} onChange={setE(`meHours${k}`)} disabled={!canEngine} />)}</div>
          <h6 className="small fw-bold">Main engine — fuel (L)</h6>
          <div className="row">{ME.map(([k, l]) => <Field key={k} label={l} id={`meFuel${k}`} value={engine[`meFuel${k}`]} onChange={setE(`meFuel${k}`)} disabled={!canEngine} />)}</div>
          <h6 className="small fw-bold">Auxiliary — hours</h6>
          <div className="row">{AE.map(([k, l]) => <Field key={k} label={l} id={`aeHours${k}`} value={engine[`aeHours${k}`]} onChange={setE(`aeHours${k}`)} disabled={!canEngine} />)}</div>
          <h6 className="small fw-bold">Auxiliary — fuel (L)</h6>
          <div className="row">{AE.map(([k, l]) => <Field key={k} label={l} id={`aeFuel${k}`} value={engine[`aeFuel${k}`]} onChange={setE(`aeFuel${k}`)} disabled={!canEngine} />)}</div>

          <h6 className="small fw-bold">Power and events (hours)</h6>
          <div className="row">
            <Field label="Inter-tug power" id="interTugHours" value={engine.interTugHours} onChange={setE('interTugHours')} disabled={!canEngine} />
            <Field label="Shore power" id="shorePowerHours" value={engine.shorePowerHours} onChange={setE('shorePowerHours')} disabled={!canEngine} />
            <Field label="Blackout" id="blackoutHours" value={engine.blackoutHours} onChange={setE('blackoutHours')} disabled={!canEngine} />
            <Field label="Power source note" id="powerSourceNote" type="text" value={engine.powerSourceNote} onChange={setE('powerSourceNote')} disabled={!canEngine} />
          </div>

          <h6 className="small fw-bold">Fuel movements</h6>
          <div className="row">
            <Field label="Supplied (L)" id="fuelSuppliedL" value={engine.fuelSuppliedL} onChange={setE('fuelSuppliedL')} disabled={!canEngine} />
            <Field label="Supplier" id="fuelSuppliedBy" type="text" value={engine.fuelSuppliedBy} onChange={setE('fuelSuppliedBy')} disabled={!canEngine} />
            <Field label="From other tug (L)" id="fuelFromTugsL" value={engine.fuelFromTugsL} onChange={setE('fuelFromTugsL')} disabled={!canEngine} />
            <Field label="From which tug" id="fuelFromTugName" type="text" value={engine.fuelFromTugName} onChange={setE('fuelFromTugName')} disabled={!canEngine} />
            <Field label="Transferred (L)" id="fuelTransferredL" value={engine.fuelTransferredL} onChange={setE('fuelTransferredL')} disabled={!canEngine} />
            <Field label="Transferred to" id="fuelTransferredTo" type="text" value={engine.fuelTransferredTo} onChange={setE('fuelTransferredTo')} disabled={!canEngine} />
          </div>

          <label htmlFor="dtr-engineRemarks" className="form-label small fw-bold text-muted">Engine remarks</label>
          <textarea id="dtr-engineRemarks" className="form-control form-control-sm mb-3" rows={3} maxLength={4000} value={engine.engineRemarks ?? ''} onChange={(ev) => setE('engineRemarks')(ev.target.value)} disabled={!canEngine} data-testid="dtr-engineRemarks" />

          <div className="bg-light border rounded p-3 small row">
            <div className="col-6 col-md-3"><span className="text-muted d-block">M/E hours</span>{totals.meHours}</div>
            <div className="col-6 col-md-3"><span className="text-muted d-block">A/E hours</span>{totals.aeHours}</div>
            <div className="col-6 col-md-3"><span className="text-muted d-block">Total consumption</span><span className="fw-bold" data-testid="dtr-total-consumption">{totals.total} L</span></div>
            <div className="col-6 col-md-3"><span className="text-muted d-block">Excess / short</span>
              <span className={`fw-bold ${totals.variance < 0 ? 'text-danger' : totals.variance > 0 ? 'text-warning' : ''}`} data-testid="dtr-variance">
                {totals.variance == null ? '—' : totals.variance === 0 ? 'Balanced' : totals.variance < 0 ? `${-totals.variance} L short` : `${totals.variance} L excess`}
              </span>
            </div>
          </div>

          {missing.engine.length > 0 && (
            <div className="alert alert-warning small mt-3 mb-0" data-testid="dtr-missing-engine">
              <div className="fw-bold">The engine section cannot be submitted yet. Still needed:</div>
              <ul className="mb-0 ps-3">{missing.engine.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          {canEngine && (
            <div className="d-flex align-items-center mt-3">
              <button className="btn btn-primary btn-sm" onClick={() => save('engine')} data-testid="dtr-save-engine">{office ? 'Save engine correction' : 'Save engine section'}</button>
              {saved.engine && <span className="text-success small ms-2" data-testid="dtr-saved-engine">Saved</span>}
            </div>
          )}
        </div>
      </div>

      {/* ---------------- Deck section ---------------- */}
      <div className="card border-0 shadow-sm mb-3" data-testid="dtr-deck">
        <div className="card-header bg-white pt-3 border-bottom-0 d-flex justify-content-between">
          <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Deck section</h6>
          <span className="small text-muted">{report.sections.deck.savedBy ? `Saved by ${report.sections.deck.savedBy}` : 'Not saved yet'}</span>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-6 col-md-3 mb-2">
              <label htmlFor="dtr-portId" className="form-label small fw-bold text-muted mb-1">Port<span className="text-danger"> *</span></label>
              <select id="dtr-portId" className="form-select form-select-sm" value={deck.portId ?? ''} onChange={(ev) => setD('portId')(ev.target.value || null)} disabled={!canDeck} data-testid="dtr-portId">
                <option value="">Select…</option>
                {ports.map((p) => <option key={p.portId} value={p.portId}>{p.portName}</option>)}
              </select>
            </div>
            <Field label="Weather" id="weather" required type="text" value={deck.weather} onChange={setD('weather')} disabled={!canDeck} />
            <Field label="Sea condition" id="seaCondition" required type="text" value={deck.seaCondition} onChange={setD('seaCondition')} disabled={!canDeck} />
            <div className="col-6 col-md-3 mb-2">
              <label htmlFor="dtr-operational" className="form-label small fw-bold text-muted mb-1">Status</label>
              <select id="dtr-operational" className="form-select form-select-sm" value={deck.operational === false ? 'no' : 'yes'} onChange={(ev) => setD('operational')(ev.target.value === 'yes')} disabled={!canDeck} data-testid="dtr-operational">
                <option value="yes">Operational</option>
                <option value="no">Not operational</option>
              </select>
            </div>
            <Field label="Latitude" id="latitude" value={deck.latitude} onChange={setD('latitude')} disabled={!canDeck} />
            <Field label="Longitude" id="longitude" value={deck.longitude} onChange={setD('longitude')} disabled={!canDeck} />
            <Field label="Position note" id="positionNote" type="text" value={deck.positionNote} onChange={setD('positionNote')} disabled={!canDeck} />
            <Field label="Location / status" id="locationNote" required type="text" value={deck.locationNote} onChange={setD('locationNote')} disabled={!canDeck} />
          </div>
          {canDeck && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mb-3"
              onClick={() => navigator.geolocation?.getCurrentPosition((pos) => { setD('latitude')(pos.coords.latitude.toFixed(6)); setD('longitude')(pos.coords.longitude.toFixed(6)); })}
            >
              Use this device&apos;s position
            </button>
          )}
          <label htmlFor="dtr-deckRemarks" className="form-label small fw-bold text-muted d-block">Deck remarks</label>
          <textarea id="dtr-deckRemarks" className="form-control form-control-sm mb-2" rows={3} maxLength={4000} value={deck.deckRemarks ?? ''} onChange={(ev) => setD('deckRemarks')(ev.target.value)} disabled={!canDeck} data-testid="dtr-deckRemarks" />
          <label htmlFor="dtr-followUps" className="form-label small fw-bold text-muted d-block">Follow-ups (requisitions, expiring documents)</label>
          <textarea id="dtr-followUps" className="form-control form-control-sm" rows={2} maxLength={4000} value={deck.followUps ?? ''} onChange={(ev) => setD('followUps')(ev.target.value)} disabled={!canDeck} data-testid="dtr-followUps" />
          {missing.deck.length > 0 && (
            <div className="alert alert-warning small mt-3 mb-0" data-testid="dtr-missing-deck">
              <div className="fw-bold">The deck section cannot be submitted yet. Still needed:</div>
              <ul className="mb-0 ps-3">{missing.deck.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          {canDeck && (
            <div className="d-flex align-items-center mt-3">
              <button className="btn btn-primary btn-sm" onClick={() => save('deck')} data-testid="dtr-save-deck">{office ? 'Save deck correction' : 'Save deck section'}</button>
              {saved.deck && <span className="text-success small ms-2" data-testid="dtr-saved-deck">Saved</span>}
            </div>
          )}
        </div>
      </div>
      {office && (
        <div className="card border-0 shadow-sm mb-3" data-testid="dtr-change-history">
          <div className="card-header bg-white pt-3 border-bottom-0">
            <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Correction history</h6>
          </div>
          <div className="card-body table-responsive pt-0">
            {(report.changes || []).length === 0 ? (
              <p className="small text-muted mb-0">No corrections have been made to this report.</p>
            ) : (
              <table className="table table-sm align-middle mb-0 small">
                <thead>
                  <tr><th>When</th><th>By</th><th>Field</th><th>Was</th><th>Now</th><th>Reason</th></tr>
                </thead>
                <tbody>
                  {report.changes.map((c) => (
                    <tr key={c.id} data-testid="dtr-change-row">
                      <td className="text-nowrap">{fmtWhen(c.changedAt)}</td>
                      <td>{c.changedBy || '—'}</td>
                      <td>{FIELD_LABEL(c.field)}</td>
                      <td>{c.oldValue ?? '—'}</td>
                      <td>{c.newValue ?? '—'}</td>
                      <td>{c.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {!office && <TugInventory tug={{ id: report.tug.id, name: report.tug.name }} />}
      {!office && <SafetyIncidents fixedTug={{ id: report.tug.id, name: report.tug.name }} />}
    </div>
  );
}
